import { consumeAppleSignInState } from "Components/Auth/Apple/openAppleAuthInBrowser";
import { AuthPageLayout } from "Components/Auth/Shared/AuthPageLayout";
import { AuthRedirectProgress } from "Components/Auth/Shared/AuthRedirectProgress";
import { StyledContainer } from "Components/Auth/Styles";
import { AppleFlow, IdentityProvider } from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { signInSsoAsync } from "State/Auth/SignIn/SignInSsoState";
import { Resources, useResource } from "Translations/Resources";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { getAppUrl } from "Utils/UrlUtils";

// Apple's `error` value when the person dismissed the Apple sign-in sheet. Not a failure
// worth a toast — just return to where they started.
const APPLE_USER_CANCELLED = "user_cancelled_authorize";

// Landing page of the browser-redirect Apple sign-in (Android shell). The API's
// apple-callback bridges Apple's form POST here as `?code=&state=&error=`; the code is
// exchanged with AppleFlow.Redirect because Apple bound it to that callback URL. The
// `state` must match the nonce issued when the flow started (openAppleAuthInBrowser) —
// otherwise the code is dropped: a code planted in a crafted URL cannot sign the device
// in to a foreign Apple account. The sign-up context (guest login, affiliate, role intent)
// travels in that same stored state, captured at tap time: this page is a fresh load on
// which the Redux user is not hydrated yet, so it must not be derived here.
export const AppleRedirectPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useResource();

  // The stored state is consumed (one-shot) on the first run, so a re-run could never
  // validate again — the ref makes the effect handle exactly one return regardless of how
  // its dependencies (translation function, navigate identity) change afterwards.
  const hasHandledReturnRef = useRef(false);

  useEffect(() => {
    if (hasHandledReturnRef.current) {
      return;
    }
    hasHandledReturnRef.current = true;

    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const issued = consumeAppleSignInState();

    if (error) {
      if (error !== APPLE_USER_CANCELLED) {
        toast.error(t(Resources.Auth.SSO.Apple.SignInFailed));
      }
      navigate(getAppUrl()("sign-in"));
      return;
    }

    if (!code || !issued || state !== issued.nonce) {
      toast.error(t(Resources.Auth.SSO.Apple.SignInFailed));
      navigate(getAppUrl()("sign-in"));
      return;
    }

    dispatch(
      signInSsoAsync.request({
        model: {
          token: code,
          identityProvider: IdentityProvider.Apple,
          ...issued.context,
          appleFlow: AppleFlow.Redirect,
        },
        navigate,
      }),
    );
  }, [location.search, dispatch, navigate, t]);

  return (
    <AuthPageLayout title={t(Resources.Auth.Redirecting)} pageType="other">
      <StyledContainer>
        <AuthRedirectProgress />
      </StyledContainer>
    </AuthPageLayout>
  );
};

export default AppleRedirectPage;
